import {validBlocks, type TweChatMessage} from '../constructor/assistantModel';
import type { EventBus } from './eventBus';

export interface SavedConversation {
  version: 1;
  identity: string;
  threadId?: string;
  messages: TweChatMessage[];
  votes: Record<string, 'up' | 'down'>;
}
export class ConversationStorage {
  readonly key: string;
  readonly enabled: boolean;
  private warned = false;
  constructor(private bus: EventBus, readonly identity: string) {
    this.key = bus.options.config?.persistence?.storageKey ?? 'twe-drinks-advisor:v1';
    this.enabled = bus.options.config?.persistence?.enabled !== false;
  }
  private failure(error: unknown) {
    if (!this.warned) {
      this.warned = true;
      this.bus.error(error, {}, 'SESSION_STORAGE_UNAVAILABLE', 'conversation_restoration');
    }
  }
  read(): SavedConversation {
    const empty: SavedConversation = { version: 1, identity: this.identity, messages: [], votes: {} };
    if (!this.enabled) return empty;
    try {
      const value = sessionStorage.getItem(this.key);
      if (!value) return empty;
      const saved = JSON.parse(value) as SavedConversation;
      if (saved.version !== 1 || saved.identity !== this.identity || !Array.isArray(saved.messages)) return empty;
      if (!saved.messages.every(m => m && typeof m.id === 'string' && typeof m.text === 'string' && ['assistant','user'].includes(m.role) && (!m.groups || (Array.isArray(m.groups) && m.groups.every(group => group && Array.isArray(group.searchResults)))))) return empty;
      return { ...saved, votes: saved.votes || {}, messages: saved.messages.map(m => ({
        ...m,
        blocks: validBlocks(m.blocks, m.groups) ? m.blocks : undefined,
        status: m.status === 'loading' || m.status === 'streaming' ? 'error' : m.status,
      })) };
    } catch (error) { this.failure(error); return empty; }
  }
  write(value: Omit<SavedConversation, 'version' | 'identity'>) {
    if (!this.enabled) return;
    try {
      if (!value.messages.length) { sessionStorage.removeItem(this.key); return; }
      sessionStorage.setItem(this.key, JSON.stringify({version: 1, identity: this.identity, ...value}));
    } catch (error) { this.failure(error); }
  }
  clear() {
    if (!this.enabled) return;
    try { sessionStorage.removeItem(this.key); } catch (error) { this.failure(error); }
  }
}
